/**
 * Users Settings Page
 *
 * Manage tenant users: invite, edit roles, remove access.
 */

import { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Avatar,
  Badge,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  KeyOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, TenantUser } from '@api/index';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

interface InviteFormValues {
  email: string;
  fullName: string;
  roleId: string;
}

interface EditRoleFormValues {
  roleId: string;
}

export default function UsersSettingsPage() {
  const queryClient = useQueryClient();
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [resettingUser, setResettingUser] = useState<TenantUser | null>(null);
  const [inviteForm] = Form.useForm<InviteFormValues>();
  const [editForm] = Form.useForm<EditRoleFormValues>();

  // Fetch tenant members
  const { data: members = [], isLoading, refetch } = useQuery({
    queryKey: ['users', 'tenant-members'],
    queryFn: () => usersApi.listTenantMembers(),
  });

  // Fetch roles for assignment
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => usersApi.listRoles(),
  });

  // Mutations
  const inviteMutation = useMutation({
    mutationFn: (data: InviteFormValues) => usersApi.inviteUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User invited successfully');
      handleCloseInviteModal();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.detail || 'Failed to invite user');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, roleId }: { id: string; roleId: string }) =>
      usersApi.updateTenantUser(id, { roleId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('Role updated');
      handleCloseEditModal();
    },
    onError: () => message.error('Failed to update role'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => usersApi.removeTenantUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success('User removed from organization');
    },
    onError: () => message.error('Failed to remove user'),
  });

  const sendResetLinkMutation = useMutation({
    mutationFn: (email: string) => usersApi.sendPasswordResetLink(email),
    onSuccess: () => {
      message.success('Password reset link sent');
      setPasswordResetModalOpen(false);
      setResettingUser(null);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.detail || 'Failed to send reset link');
    },
  });

  const handleOpenInvite = useCallback(() => {
    inviteForm.resetFields();
    setInviteModalOpen(true);
  }, [inviteForm]);

  const handleCloseInviteModal = useCallback(() => {
    setInviteModalOpen(false);
    inviteForm.resetFields();
  }, [inviteForm]);

  const handleOpenEdit = useCallback(
    (tenantUser: TenantUser) => {
      setEditingUser(tenantUser);
      editForm.setFieldsValue({
        roleId: tenantUser.roleId,
      });
      setEditModalOpen(true);
    },
    [editForm]
  );

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingUser(null);
    editForm.resetFields();
  }, [editForm]);

  const handleInvite = useCallback(async () => {
    try {
      const values = await inviteForm.validateFields();
      inviteMutation.mutate(values);
    } catch {
      // Form validation error
    }
  }, [inviteForm, inviteMutation]);

  const handleUpdateRole = useCallback(async () => {
    if (!editingUser) return;
    try {
      const values = await editForm.validateFields();
      updateRoleMutation.mutate({ id: editingUser.id, roleId: values.roleId });
    } catch {
      // Form validation error
    }
  }, [editForm, editingUser, updateRoleMutation]);

  const handleRemove = useCallback(
    (userId: string) => {
      removeMutation.mutate(userId);
    },
    [removeMutation]
  );

  const handleOpenResetPassword = useCallback((user: TenantUser) => {
    setResettingUser(user);
    setPasswordResetModalOpen(true);
  }, []);

  const handleSendResetLink = useCallback(() => {
    if (resettingUser?.user?.email) {
      sendResetLinkMutation.mutate(resettingUser.user.email);
    }
  }, [resettingUser, sendResetLinkMutation]);

  const getInitials = (name?: string) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      '#f56a00',
      '#7265e6',
      '#ffbf00',
      '#00a2ae',
      '#1890ff',
      '#eb2f96',
      '#52c41a',
      '#722ed1',
    ];
    const index = (id || 'x').charCodeAt(0) % colors.length;
    return colors[index];
  };

  const columns: ColumnsType<TenantUser> = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Badge
            dot={record.isActive}
            status={record.isActive ? 'success' : 'default'}
            offset={[-4, 32]}
          >
            <Avatar style={{ backgroundColor: getAvatarColor(record.id) }}>
              {getInitials(record.user?.fullName)}
            </Avatar>
          </Badge>
          <div>
            <div>
              <Text strong>{record.user?.fullName || 'Unknown'}</Text>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.user?.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'roleName',
      width: 150,
      render: (role: string) => <Tag color="blue">{role}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      width: 100,
      render: (isActive: boolean) =>
        isActive ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: 'Joined',
      dataIndex: 'createdAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit Role">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Reset Password">
            <Button
              type="text"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleOpenResetPassword(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove user from organization?"
            description="They will lose access to this organization's data."
            onConfirm={() => handleRemove(record.id)}
            okText="Remove"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Remove">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Users
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenInvite}>
            Invite User
          </Button>
        </Space>
      </div>

      <Table
        dataSource={members}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={members.length > 20 ? { pageSize: 20 } : false}
        size="middle"
      />

      {/* Invite Modal */}
      <Modal
        title="Invite User"
        open={inviteModalOpen}
        onOk={handleInvite}
        onCancel={handleCloseInviteModal}
        confirmLoading={inviteMutation.isPending}
        okText="Send Invite"
        destroyOnClose
      >
        <Form form={inviteForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Please enter their name' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="roleId"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              {roles.map((role: any) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                  {role.description && (
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                      - {role.description}
                    </Text>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Role Modal */}
      <Modal
        title={`Edit Role: ${editingUser?.user?.fullName}`}
        open={editModalOpen}
        onOk={handleUpdateRole}
        onCancel={handleCloseEditModal}
        confirmLoading={updateRoleMutation.isPending}
        okText="Update"
        destroyOnClose
        width={400}
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="roleId"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              {roles.map((role: any) => (
                <Select.Option key={role.id} value={role.id}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        title={
          <span>
            <KeyOutlined style={{ marginRight: 8 }} />
            Reset Password
          </span>
        }
        open={passwordResetModalOpen}
        onOk={handleSendResetLink}
        onCancel={() => {
          setPasswordResetModalOpen(false);
          setResettingUser(null);
        }}
        confirmLoading={sendResetLinkMutation.isPending}
        okText="Send Reset Link"
        okButtonProps={{ icon: <SendOutlined /> }}
        destroyOnClose
        width={400}
      >
        <div style={{ marginTop: 16 }}>
          <Text>
            Send a password reset link to{' '}
            <Text strong>{resettingUser?.user?.fullName}</Text>?
          </Text>
          <div style={{ marginTop: 12, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6 }}>
            <MailOutlined style={{ marginRight: 8, color: '#888' }} />
            <Text type="secondary">{resettingUser?.user?.email}</Text>
          </div>
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              The user will receive an email with a link to reset their password.
              The link expires in 1 hour.
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
}
